import classNames from 'classnames';
import {ComponentType, ReactNode, memo, useMemo} from 'react';

import {Accordion} from '@macmillanplatform/ui-core';
import type {PropsWithClassName} from '@macmillanplatform/ui-core/types';

import './index.css';

export interface AccordionPageSection<
  Contents extends object,
> extends PropsWithClassName {
  title: ReactNode;
  iconClassName: string;
  component: ComponentType<Contents>;
  contents: Contents[];
}

export interface AccordionPageProps<Contents extends object> {
  sections: Record<string, AccordionPageSection<Contents>>;
}

const getAccordionSections = <Contents extends object>(
  sections: Record<string, AccordionPageSection<Contents>>
) =>
  Object.entries(sections).map(([key, section]) => {
    const {
      component: Component,
      title,
      className,
      iconClassName,
      contents,
    } = section;

    return {
      key,
      className: classNames('AccordionPage__section', className),
      title: (
        <span className="AccordionPage__section-label">
          <i className={classNames('fa-duotone', iconClassName)} />
          {title}
        </span>
      ),
      contents: contents.map((values, index) => (
        <Component key={index} {...values} />
      )),
      selected: true,
    };
  });

const AccordionPage = <Contents extends object>(
  props: AccordionPageProps<Contents>
) => {
  const {sections} = props;

  const pageSections = useMemo(
    () => getAccordionSections(sections),
    [sections]
  );

  return (
    <Accordion withLine className="AccordionPage" sections={pageSections} />
  );
};

export default memo(AccordionPage);
